import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ProductQuoteRequestItem, ProductQuoteResult } from '@typesApp/ProductQuote'
import { useLazyGetProductQuoteQuery } from '@redux/queries/claimRecords'
import { claimRecordIdSelector } from '@redux/slices/claimStatusSlice'
import { connectorProductQuoteError } from '@redux/slices/connectorSlice'
import { CommerceProductQuoteRequestItem } from '../types'

type UseProductQuoteReturn = {
  isLoading: boolean
  isError: boolean
  requestQuote: (request: CommerceProductQuoteRequestItem[]) => Promise<ProductQuoteResult>
}

export const useProductQuoteService = (): UseProductQuoteReturn => {
  const dispatch = useDispatch()
  const claimRecordId = useSelector(claimRecordIdSelector)
  const [triggerRequest, { isLoading, isError }] = useLazyGetProductQuoteQuery()

  const mapCommerceProducts = (products: CommerceProductQuoteRequestItem[]): ProductQuoteRequestItem[] => {
    return (products ?? []).map(product => {
      const { productType, quantity, unitFullPrice } = product

      return {
        productType,
        ecommerceId: product.productIdentifier,
        ecommerceIdType: product.productType === 'FRAME' ? 'UPC' : 'RXC_ID',
        quantity,
        unitFullPrice,
      }
    })
  }

  const requestQuote = useCallback(
    (request: CommerceProductQuoteRequestItem[]): Promise<ProductQuoteResult> => {
      if (!claimRecordId) {
        return Promise.resolve({ productQuotes: [] })
      }

      const products = mapCommerceProducts(request)

      return new Promise<ProductQuoteResult>(resolve => {
        triggerRequest({ claimRecordId, products })
          .unwrap()
          .then(result => resolve(result))
          .catch(error => {
            console.error('Request product quote error', error)
            dispatch(connectorProductQuoteError(true))
            resolve({ productQuotes: [], isError: true })
          })
      })
    },
    [dispatch, claimRecordId]
  )

  return {
    isLoading,
    isError,
    requestQuote,
  }
}
